import api from './api'

// Search result item interface
export interface SearchResult {
  id: string
  type: 'customer' | 'prospect' | 'reseller' | 'contact' | 'invoice' | 'quote' | 'file'
  title: string
  subtitle: string
  url: string
  icon: string
}

// Search response interface
export interface SearchResponse {
  results: SearchResult[]
  query: string
  total_count: number
  message?: string
}

/** Which result types the viewer may see — one flag per type, each the SAME
 *  page gate the CRM sidebar uses (usePermissions().canAccessX). */
export type SearchAccess = Record<SearchResult['type'], boolean>

/**
 * crm-api answers with URLs relative to the CRM's OLD standalone root
 * (`/customers/7`, `/administration/resellers/3`) — it predates the CRM being
 * mounted under `/crm` in the dashboard. Following one verbatim leaves the CRM
 * entirely, so every result link is re-rooted here. Idempotent, so a server
 * that one day sends `/crm/...` keeps working.
 */
export function searchResultHref(url: string): string {
  const path = url.startsWith('/') ? url : `/${url}`
  if (path === '/crm' || path.startsWith('/crm/')) return path
  return `/crm${path}`
}

/**
 * Drop every result the viewer could not open. crm-api already narrows the ROWS
 * (data scope, the reseller wall, the dollar amount on invoice subtitles), but
 * it still returns invoice and quote hits to a viewer whose session has no
 * Invoices page — i.e. one without `crm:financials` — and a hit that lands on
 * "Access Denied" is worse than no hit. So a group shows only if its page is in
 * the sidebar. Server order is preserved: the payload is relevance-ranked.
 */
export function visibleSearchResults(results: SearchResult[], access: SearchAccess): SearchResult[] {
  return results.filter((r) => access[r.type] === true)
}

// Global search API service
export const searchApi = {
  // Global search across all entities
  search: async (query: string): Promise<SearchResponse> => {
    const response = await api.get<SearchResponse>('/search', {
      params: { q: query }
    })
    return response.data
  }
}

export default searchApi
